import { ThemeEvents, VariantUpdateEvent } from '@theme/events';

/**
 * WMS-driven product inventory status block.
 * Shows only AVAILABLE / COMING SOON / ALL_IN_CART (never Shopify Low Stock / Sold Out).
 * Loading uses skeleton placeholders — never temporary Coming Soon.
 */
class ProductInventory extends HTMLElement {
  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {number} */
  #syncSeq = 0;

  /** @type {(() => void) | null} */
  #unsubscribe = null;

  connectedCallback() {
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;
    const closestSection = this.closest('.shopify-section, dialog');
    closestSection?.addEventListener(ThemeEvents.variantUpdate, this.updateInventory, { signal });
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate, { signal });

    const inventoryApi = /** @type {any} */ (window).WMSInventory || /** @type {any} */ (window).AOneWmsAvailability;
    if (inventoryApi?.subscribe) {
      this.#unsubscribe = inventoryApi.subscribe(() => {
        this.#syncFromWms();
      });
    }

    this.#syncFromWms();
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#abortController = null;
    if (typeof this.#unsubscribe === 'function') this.#unsubscribe();
    this.#unsubscribe = null;
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  updateInventory = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = String(event.detail.data.newProduct.id);
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const sku = event.detail.resource?.sku ? String(event.detail.resource.sku).trim() : '';
    if (sku) this.dataset.wmsVariantSku = sku;
    this.#syncFromWms();
  };

  #onCartUpdate = () => {
    this.#syncFromWms();
  };

  #renderLoading(textEl) {
    const inventoryApi = /** @type {any} */ (window).WMSInventory || /** @type {any} */ (window).AOneWmsAvailability;
    textEl.innerHTML =
      inventoryApi && typeof inventoryApi.getInventorySkeletonMarkup === 'function'
        ? inventoryApi.getInventorySkeletonMarkup('badge')
        : '<span class="aone-inventory-skeleton aone-inventory-skeleton--badge" aria-hidden="true"></span><span class="visually-hidden">Loading availability</span>';
    this.dataset.wmsStatus = 'loading';
    this.setAttribute('aria-busy', 'true');
  }

  async #syncFromWms() {
    const inventoryApi = /** @type {any} */ (window).WMSInventory || /** @type {any} */ (window).AOneWmsAvailability;
    const textEl = this.querySelector('[data-wms-status-badge], .product-inventory__text');
    if (!(textEl instanceof HTMLElement) || !inventoryApi) return;

    const seq = ++this.#syncSeq;
    const productId = String(this.dataset.productId || '').trim();
    const sku =
      String(this.dataset.wmsVariantSku || '').trim() ||
      String(
        this.closest('product-form-component')?.dataset?.wmsVariantSku ||
          document.querySelector(`product-form-component[data-product-id="${productId}"]`)?.dataset?.wmsVariantSku ||
          ''
      ).trim();

    this.#renderLoading(textEl);

    if (productId && typeof inventoryApi.ensureProductsLoaded === 'function') {
      await inventoryApi.ensureProductsLoaded([productId]);
    }
    if (seq !== this.#syncSeq) return;

    try {
      if (typeof inventoryApi.ensureCartSerialsSynced === 'function') {
        const force = typeof inventoryApi.isCartStateReady === 'function' && !inventoryApi.isCartStateReady();
        await inventoryApi.ensureCartSerialsSynced(force);
      }
    } catch (_error) {
      /* keep loading/error path below */
    }
    if (seq !== this.#syncSeq) return;

    if (typeof inventoryApi.isCartStateReady === 'function' && !inventoryApi.isCartStateReady()) {
      this.#renderLoading(textEl);
      return;
    }

    let quantity = null;
    let rawQuantity = null;
    let loading = false;
    let errored = false;

    if (sku && typeof inventoryApi.getInventoryBySku === 'function') {
      const inventory = inventoryApi.getInventoryBySku(sku);
      if (inventory?.loading) {
        loading = true;
      } else if (inventory?.error) {
        errored = true;
      } else {
        quantity = Math.max(0, Number(inventory?.effectiveAvailableCount ?? inventory?.quantity) || 0);
        rawQuantity = Math.max(0, Number(inventory?.rawAvailableCount ?? quantity) || 0);
      }
    } else if (productId && typeof inventoryApi.getProductAvailability === 'function') {
      const product = inventoryApi.getProductAvailability(productId);
      if (product?.status === 'loading' || product?.status === 'idle' || product?.loading) {
        loading = true;
      } else if (product?.status === 'error') {
        errored = true;
      } else {
        quantity = Math.max(0, Math.floor(Number(product?.totalAvailableCount) || 0));
        rawQuantity = Math.max(
          0,
          Math.floor(Number(product?.rawWmsTotalAvailableCount ?? quantity) || 0)
        );
      }
    }

    if (loading) {
      this.#renderLoading(textEl);
      return;
    }

    this.removeAttribute('aria-busy');

    if (errored) {
      textEl.textContent = inventoryApi.LABEL_UNAVAILABLE || 'AVAILABILITY UNAVAILABLE';
      this.dataset.wmsStatus = 'error';
      return;
    }

    const safeQty = Math.max(0, Number(quantity) || 0);
    const safeRaw = Math.max(0, Number(rawQuantity) || 0);
    const status =
      typeof inventoryApi.getStorefrontStatus === 'function'
        ? inventoryApi.getStorefrontStatus(safeRaw, safeQty)
        : safeQty > 0
          ? 'AVAILABLE'
          : safeRaw > 0
            ? 'ALL_IN_CART'
            : 'COMING_SOON';

    if (status === 'AVAILABLE') {
      textEl.textContent = safeQty > 0 ? `AVAILABLE QTY: ${safeQty}` : inventoryApi.LABEL_AVAILABLE || 'AVAILABLE';
      this.dataset.wmsStatus = 'available';
    } else if (status === 'ALL_IN_CART') {
      textEl.textContent = 'AVAILABLE QTY: 0';
      this.dataset.wmsStatus = 'all-in-cart';
    } else {
      textEl.textContent = inventoryApi.LABEL_COMING_SOON || 'COMING SOON';
      this.dataset.wmsStatus = 'coming-soon';
    }
  }
}

if (!customElements.get('product-inventory')) {
  customElements.define('product-inventory', ProductInventory);
}
